import { toJS, makeAutoObservable } from 'mobx';
import {t} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import {fetchModels} from 'shared/store';
import $ from 'jquery';
import _ from 'lodash';
import TimelineSectionViewModel from './TimelineSectionViewModel';
import TimelineEntry from 'main/stores/recruiting/TimelineEntry';
import Candidate from 'main/stores/recruiting/Candidate';
import CandidateTag from 'main/stores/recruiting/CandidateTag';
import InterviewGuideAnswer from 'main/stores/interview_guides/InterviewGuideAnswer';
import {successAlert, redirect} from 'shared/tools';
import {setupAutosaveDraft} from 'main/tools';
import {Offer} from 'main/stores/offers';
import {Company} from 'main/stores/company';

class CandidateDetailsState {
  hiringFunnelState;
  enforceRequiredFields = false;

  errors = {};
  editCandidateModalOpen = false;
  deleteCandidateModalOpen = false;
  movePositionModalOpen = false;
  createdSection = null;
  newFeedback = TimelineEntry.createFeedback();
  newFeedbackErrors = {};
  editedEntry;
  editedEntryErrors = {};
  newEmail = TimelineEntry.createEmail();
  newEmailErrors = {};
  newInterviewGuide = TimelineEntry.createInterviewGuideResponse();
  newInterviewGuideErrors = {};
  editingInterviewInvite = null;
  interviewInviteToDestroy = null;
  newPositionId = null;
  newPositionChangeText = '';
  entryBeingEdited = null;
  __candidateViewModel;
  editingCandidate;
  emailModalOpen = false;
  openedEmail = {};
  newUploadText = '';
  newFiles = '';
  attachments = [];
  availableTags = [];
  selectedTags = [];
  isLoadingTags = false;
  editTagsModalOpen = false;
  isUploading = false;
  offerPreviewModalOpen = false;
  previewedOffer;
  company;
  mousedOverStar = false;
  mousedOverStarIndex;
  templateCounter = 0;
  bulkAddingTags = false;
  bulkMovingCandidates = false;

  constructor(hiringFunnelState) {
    makeAutoObservable(this);

    this.hiringFunnelState = hiringFunnelState;
  }

  async loadTags() {
    this.isLoadingTags = true;
    const tags = await fetchModels(
      endpoints.RECRUITING.CANDIDATE_TAGS,
      types.RECRUITING.CANDIDATE_TAG
    );
    this.isLoadingTags = false;
    this.availableTags = tags.map(model => new CandidateTag(model));
  }

  isBeingEdited(entry) {
    return this.entryBeingEdited === entry;
  }

  getOrCreateCurrentSection() {
    let currentSection = this.currentSection;
    if (!currentSection && !this.createdSection) {
      currentSection = new TimelineSectionViewModel(this.candidate);
      currentSection.stage = this.candidate.currentStage;
      this.createdSection = currentSection;
    }
    return currentSection;
  }

  updateEntryState(entry, errors, model) {
    if (model) {
      const isNew = entry.isNew;
      entry.merge(model);

      if(entry.autosaver) {
        entry.autosaver.clearAutosaver();
      }

      this.cancelInput();
      if (isNew) {
        this.candidate.timelineEntries.push(entry);
      }

      return true;
    }

    this.newFeedbackErrors = errors;
  }

  createNewInterviewGuide() {
    redirect('/recruiting/interview-guides/edit/');
  }

  openDestroyInterviewInviteModal(invite) {
    this.interviewInviteToDestroy = invite;
  }

  async destroyInterviewInvite() {
    const {errors} = await this.hiringFunnelState.store.destroy(this.interviewInviteToDestroy);

    if (_.isEmpty(errors)) {
      successAlert(t('recruiting.alerts.Interview invite cancelled'));
      this.closeDestroyInterviewInviteModal();
      await this.hiringFunnelState.loadCandidate(this.candidate.id);
    }
  }

  async showEmail(subject) {
    this.cancelInput();

    await setupAutosaveDraft(
      this.newEmail,
      {id: this.candidate.id, type: types.RECRUITING.TIMELINE.EMAIL}
    );

    if (subject) {
      this.newEmail.subject = subject;
    }

    const currentSection = this.getOrCreateCurrentSection();
    currentSection.showEmailInput = true;
  }

  async showInterviewGuide() {
    this.cancelInput();

    await setupAutosaveDraft(
      this.newInterviewGuide,
      {id: this.candidate.id, type: types.RECRUITING.TIMELINE.INTERVIEW_GUIDE_RESPONSE}
    );

    const currentSection = this.getOrCreateCurrentSection();
    currentSection.showInterviewGuide = true;
  }

  async addInterviewGuide() {
    const {errors, model} = await this.hiringFunnelState.saveEntry(this.newInterviewGuide);
    const success = this.updateEntryState(this.newInterviewGuide, errors, model);
    if (success) {
      successAlert(t('recruiting.alerts.Feedback successfully added'));
      this.cancelInterviewGuide();
      await this.setupNewInterviewGuideAutosave();
    }
  }

  selectInterviewGuide(guide) {
    if (this.newInterviewGuide.hasAnswerContent) {
      if (window.confirm(t('components.warn_unsaved_changes.Are you sure you want to change your interview guide? All unsaved changes will be lost.'))) {
        this.useInterviewGuide(guide);
      }
    } else {
      this.useInterviewGuide(guide);
    }
  }

  useInterviewGuide(guide) {
    this.newInterviewGuide.interviewGuide = guide;
    this.newInterviewGuide.answers = [];

    guide.questions.forEach(question => {
      const answer = new InterviewGuideAnswer();
      answer.merge({question: question});
      this.newInterviewGuide.answers.push(answer);
    });

    this.newInterviewGuide.autosaver.autosave();
  }

  async setupNewFeedbackAutosave() {
    if (this.candidate) {
      await setupAutosaveDraft(
        this.newFeedback,
        {id: this.candidate.id, type: types.RECRUITING.TIMELINE.FEEDBACK}
      );
    }
  }

  async setupNewInterviewGuideAutosave() {
    if (this.candidate) {
      await setupAutosaveDraft(
        this.newInterviewGuide,
        {id: this.candidate.id, type: types.RECRUITING.TIMELINE.INTERVIEW_GUIDE_RESPONSE}
      );
    }
  }

  showFileUpload() {
    this.cancelInput();

    const currentSection = this.getOrCreateCurrentSection();
    currentSection.showFileUpload = true;

    setTimeout(() => {
      $('html, body').animate({scrollTop: $('.js-file-uploads').offset().top - 150}, 500);
      $('.js-file-uploads').focus();
    }, 0);
  }

  reply(email) {
    const replyPrefix = t('recruiting.hiring_funnel.reply_prefix');
    const subject = email.subject || '';

    if (subject.startsWith(replyPrefix)) {
      this.showEmail(subject);
    } else {
      this.showEmail(`${replyPrefix} ${subject}`);
    }
  }

  calculateQuestionRequired(question) {
    return this.enforceRequiredFields && question.required;
  }

  async addEmail() {
    const {errors, model} = await this.hiringFunnelState.saveEntry(this.newEmail);
    const success = this.updateEntryState(this.newEmail, errors, model);
    if (success) {
      successAlert(t('recruiting.alerts.Email sent to candidate'));
    }
    this.newEmailErrors = errors;
  }

  selectEmailTemplate(template) {
    if (this.newEmail.subject || this.newEmail.strippedBody) {
      if (window.confirm(t('components.warn_unsaved_changes.Are you sure you want to change your template? All unsaved changes will be lost.'))) {
        this.useEmailTemplate(template);
      }
    } else {
      this.useEmailTemplate(template);
    }
  }

  async useEmailTemplate(template) {
    this.newEmail.subject = template.subject;
    this.newEmail.lexicalState = template.lexicalState;
    this.newEmail.body = template.richText;
    this.templateCounter += 1;
    this.newEmail.autosaver.autosave();
  }

  async addFeedback() {
    if (!this.canAddNewSentiment) {
      this.newFeedback.sentiment = null;
    }
    const {errors, model} = await this.hiringFunnelState.saveEntry(this.newFeedback);
    const success = this.updateEntryState(this.newFeedback, errors, model);
    if (success) {
      successAlert(t('recruiting.alerts.Feedback successfully added'));
      this.cancelFeedback();
      await this.setupNewFeedbackAutosave();
    }
  }

  async addFileUploads() {
    const upload = TimelineEntry.createUpload({text: this.newUploadText, attachments: this.newFiles});
    const {errors, model} = await this.hiringFunnelState.saveEntry(upload);
    const success = this.updateEntryState(upload, errors, model);

    if (success) {
      successAlert(t('recruiting.alerts.Documents successfully added'));
    }
  }

  async savePositionChange() {
    this.errors = {};
    const position = _.find(this.hiringFunnelState.availablePositions, { id: this.newPositionId });

    if (this.bulkMovingCandidates) {
      const movedCount = await this.hiringFunnelState.bulkAction({
        newPositionId: this.newPositionId,
        newPositionText: this.newPositionChangeText
      });
      this.closeMovePositionModal();
      successAlert(t('recruiting.alerts.BULK_MOVE', {
        count: movedCount, new: position.title
      }));
    } else {
      await this.hiringFunnelState.changePosition({
        position: position,
        changePositionText: this.newPositionChangeText
      });
    }
  }

  async updateEntry(entry) {
    const {errors, model} = await this.hiringFunnelState.saveEntry(this.editedEntry);
    const success = this.updateEntryState(entry, errors, model);
    if (success) {
      successAlert(t('recruiting.alerts.Successfully updated'));
    }
  }

  async updateEmail(email) {
    this.newEmail.merge(email);
    this.newEmail.autosaver.autosave();
  }

  async updateEmailBody(body) {
    this.newEmail.body = body.html;
    this.newEmail.lexicalState = body.lexicalState;

    if (this.newEmail.autosaver) {
      this.newEmail.autosaver.autosave();
    }
  }

  async updateNewFeedbackText(text) {
    this.newFeedback.text = text.html;
    this.newFeedback.lexicalState = text.state;

    if (this.newFeedback.autosaver) {
      this.newFeedback.autosaver.autosave();
    }
  }

  async updateNewFeedback(feedback) {
    this.newFeedback.merge(feedback);
    this.newFeedback.autosaver.autosave();
  }

  async updateEditedEntry(feedback) {
    this.editedEntry.merge(feedback);
  }

  async updateEditedFeedbackText(text) {
    this.editedEntry.text = text.html;
    this.editedEntry.lexicalState = text.state;
  }

  async editEntry(entry) {
    this.entryBeingEdited = entry;

    if (entry.isInterviewGuideResponse) {
      this.editedEntry = TimelineEntry.createInterviewGuideResponse(entry);
    } else {
      this.editedEntry = TimelineEntry.createFeedback(entry);
      if (!entry.sentiment && this.stageHasSentiment(entry.funnelStage.id)) {
        this.editedEntry.sentiment = null;
      }
    }
  }

  cancelInput() {
    for (const section of this.timelineSections) {
      section.showEmailInput = false;
      section.showFileUpload = false;
      section.showInterviewGuide = false;
      if (this.createdSection) {
        this.createdSection = null;
      }
    }
    this.newEmail = TimelineEntry.createEmail();
    this.newEmailErrors = {};
    this.newInterviewGuide = TimelineEntry.createInterviewGuideResponse();
    this.newInterviewGuideErrors = {};
    this.newUploadText = '';
    this.newFiles = [];
    this.entryBeingEdited = null;
  }

  cancelFeedback() {
    this.newFeedback = TimelineEntry.createFeedback();
    this.newFeedbackErrors = {};
  }

  cancelInterviewGuide() {
    this.newInterviewGuide = TimelineEntry.createInterviewGuideResponse();
    this.newInterviewGuideErrors = {};
  }

  openEditCandidateModal() {
    this.editingCandidate = new Candidate(this.candidate);
    this.editCandidateModalOpen = true;
  }

  closeEditCandidateModal() {
    this.editCandidateModalOpen = false;
  }

  openDeleteCandidateModal() {
    this.deleteCandidateModalOpen = true;
  }

  closeDeleteCandidateModal() {
    this.deleteCandidateModalOpen = false;
  }

  openEditTagsModal(bulkAddingTags = false) {
    this.loadTags();
    this.editTagsModalOpen = true;
    this.bulkAddingTags = bulkAddingTags;
    if (bulkAddingTags) {
      this.selectedTags = [];
    } else {
      this.selectedTags = toJS(this.candidate.candidateTags);
    }
  }

  closeEditTagsModal() {
    this.editTagsModalOpen = false;
  }

  onTagChange(e) {
    if (e.action === 'add') {
      this.addTag(e.value);
    } else if (e.action === 'remove') {
      this.removeTag(e.value);
    }
  }

  addTag(tag) {
    this.selectedTags.push(new CandidateTag({name: tag}));
  }

  removeTag(tag) {
    _.remove(this.selectedTags, {name: tag});
  }

  async saveTags() {
    if (this.bulkAddingTags) {
      const tagAddedCount = await this.hiringFunnelState.bulkAction({addedTags: this.selectedTags});
      this.closeEditTagsModal();
      successAlert(t('recruiting.alerts.BULK_ADD_TAGS', { count: tagAddedCount }));
    } else {
      this.candidate.candidateTags = this.selectedTags;
      const candidate = this.candidate.pick(['candidateTags']);
      const {model} = await this.hiringFunnelState.store.patch(candidate);

      if (model) {
        this.candidate.merge(model);
        this.closeEditTagsModal();
      }
    }
  }

  async openOfferPreviewModal(offerId) {
    this.offerPreviewModalOpen = true;
    await this.hiringFunnelState.store._compose([
      endpoints.OFFER.with(offerId),
      endpoints.CURRENT_COMPANY
    ]);
    this.company = new Company(this.hiringFunnelState.store._getSingle(types.COMPANY));
    this.previewedOffer = new Offer(this.hiringFunnelState.store._getSingle(types.OFFER, {id: offerId.toString()}));
  }

  closeOfferPreviewModal() {
    this.offerPreviewModalOpen = false;
  }

  async deleteEntry(entry) {
    const mostRecentRating = this.candidate.mostRecentRating;

    if (this.createdSection) {
      this.createdSection.entries.remove(entry);
    } else {
      this.candidate.timelineEntries.remove(entry);
    }
    await this.hiringFunnelState.destroyEntry(entry);

    if (entry.isRating && entry.id === mostRecentRating.id) {
      if (this.candidate.mostRecentRating) {
        this.candidate.score = this.candidate.mostRecentRating.score;
      } else {
        this.candidate.score = null;
      }
    }

    successAlert(t('recruiting.alerts.Successfully deleted'));
  }

  async deleteCandidate() {
    await this.hiringFunnelState.deleteCandidate(this.candidate);
    this.closeDeleteCandidateModal();
  }

  openMovePositionModal(bulkMovingCandidates = false) {
    this.newPositionId = _.head(this.hiringFunnelState.availablePositions).id;
    this.bulkMovingCandidates = bulkMovingCandidates;
    this.movePositionModalOpen = true;
  }

  closeMovePositionModal() {
    this.movePositionModalOpen = false;
    this.newPositionId = null;
    this.newPositionChangeText = null;
  }

  openEmailModal(email) {
    this.openedEmail = email;
    this.emailModalOpen = true;
  }

  closeEmailModal() {
    this.emailModalOpen = false;
  }

  onUploadStarted() {
    this.isUploading = true;
  }

  onUploadFinished() {
    this.isUploading = false;
  }

  async rateCandidate(score) {
    this.candidate.merge({score});
    const candidate = this.candidate.pick(['score']);
    const {model} = await this.hiringFunnelState.store.patch(candidate);

    if (model) {
      this.candidate.merge(model);
      successAlert(t('recruiting.alerts.Candidate successfully rated'));
    }
  }

  get candidateViewModel() {
    return this.__candidateViewModel;
  }

  set candidateViewModel(candidateViewModel) {
    this.createdSection = null;
    this.attachments = [];
    this.__candidateViewModel = candidateViewModel;
  }

  get candidate() {
    if (!this.candidateViewModel) return null;

    return this.candidateViewModel.candidate;
  }

  get timelineSections() {
    const entriesByPosition = _.groupBy(
      this.candidate.timelineEntries, entry => entry.funnelStage.positionId
    );
    const sortedEntriesByPosition = _.reverse(
      _.sortBy(entriesByPosition, entries => _.maxBy(entries, entry => entry.createdAt).createdAt)
    );
    const sectionsByPositions = _.map(sortedEntriesByPosition, entries => {
      const entriesByStage = _.groupBy(entries, entry => entry.funnelStage.id);
      const sections = _.map(entriesByStage, entries =>
        new TimelineSectionViewModel(this.candidate, entries)
      );
      return _.orderBy(sections, ['stage.order'], ['desc']);
    });
    const sections = _.flatten(sectionsByPositions);

    return this.createdSection ? [this.createdSection, ...sections] : sections;
  }

  get isEmptyTimeline() {
    return !this.timelineSections.length;
  }

  get currentSection() {
    return _.find(
      this.timelineSections, {
        stage: {
          id: this.candidate.currentStage.id
        }
      }
    );
  }

  stageHasSentiment(funnelStageId) {
    return _.chain(this.candidate)
      .get('timelineEntries')
      .filter({
        user: {
          id: auth.user.id
        },
        funnelStage: {
          id: funnelStageId
        },
        isFeedback: true
      })
      .reject({sentiment: null})
      .value().length > 0;
  }

  get canAddNewSentiment() {
    return !this.stageHasSentiment(this.candidate.currentStage.id);
  }

  get canUpdateSentiment() {
    if (this.editedEntry.sentiment) {
      return true;
    }

    return !this.stageHasSentiment(this.entryBeingEdited.funnelStage.id);
  }

  get isPositiveSentiment() {
    return this.newFeedback.sentiment === 'positive';
  }

  get isNegativeSentiment() {
    return this.newFeedback.sentiment === 'negative';
  }

  get funnelPickerDisabled() {
    return _.get(this.candidateViewModel, 'candidate.disqualified', false);
  }

  get showInterviewGuideButton() {
    return (!_.isEmpty(this.hiringFunnelState.interviewGuides) || auth.hasAccess('::MANAGE_ATS'));
  }

  get showScheduleInterviewButton() {
    return (!_.isEmpty(this.hiringFunnelState.interviewSchedules) || auth.hasAccess('::MANAGE_ATS'));
  }

  get canHireEmployees() {
    return auth.hasAccess('::HIRE_EMPLOYEES');
  }
}

export default CandidateDetailsState;
